import { ArrowRight, Image as ImageIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

export type BlogCardSize = 'small' | 'large'

export interface BlogCardProps {
  /** "small" spans one grid column; "large" spans two — for landscape photos. */
  size?: BlogCardSize
  /** Omit while the real photo isn't ready yet — renders a placeholder in its place. */
  imageSrc?: string
  imageAlt?: string
  title: string
  href: string
  onClick?: () => void
  className?: string
}

/**
 * Blog/article teaser card: photo, title, and a "Learn more" link. Meant to
 * sit inside a grid — `size="large"` spans two columns down to the `lg`
 * breakpoint, matching the blog section's grid-to-1-column collapse.
 */
export function BlogCard({
  size = 'small',
  imageSrc,
  imageAlt = '',
  title,
  href,
  onClick,
  className,
}: BlogCardProps) {
  const large = size === 'large'

  return (
    <article
      className={cn(
        'border-ink-100 flex flex-col gap-4 overflow-hidden rounded-sm border',
        large && 'col-span-2 max-lg:col-span-1',
        className
      )}
    >
      <div className={cn('bg-ink-100 overflow-hidden rounded-sm', large ? 'aspect-[32/9]' : 'aspect-4/3')}>
        {imageSrc ? (
          <img src={imageSrc} alt={imageAlt} className="size-full object-cover" />
        ) : (
          <span className="text-ink-400 flex size-full items-center justify-center" aria-hidden="true">
            <ImageIcon className="size-8" />
          </span>
        )}
      </div>
      <h4 className="px-4">{title}</h4>
      <a
        className="group/link text-brand-800 hover:text-brand-900 inline-flex items-center gap-2 px-4 pb-4 text-sm font-semibold"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
      >
        Learn more
        <ArrowRight
          aria-hidden="true"
          className="size-4 transition-transform duration-150 group-hover/link:translate-x-0.5"
        />
      </a>
    </article>
  )
}
